import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";

interface TableFiltersPersistContextType {
    updateFilters: (value: string) => void;
    clearFilters: () => void;
    getFilters: <T>(detailsPath: string[]) => T;
}

const TableFiltersPersistContext = createContext<TableFiltersPersistContextType | null>(null);

export default function TableFiltersPersistProvider({ children }: { children: React.ReactNode }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const detailsPathsRef = useRef<string[]>([]);
    const { pathname } = useLocation();
    const actualSection = useRef<string>(pathname);
    const lastPersistedValueRef = useRef<string | null>(null);

    const updateFilters = useCallback((value: string) => {
        if (actualSection.current !== pathname) {
            actualSection.current = pathname;
            return;
        }
        const currentValue = searchParams.get('filters');

        if (currentValue === value || (!value && !currentValue)) {
            return;
        }

        if (!currentValue && lastPersistedValueRef.current && value === lastPersistedValueRef.current) {
            return;
        }

        setSearchParams((currentSearchParams) => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);

            if (value) {
                nextSearchParams.set('filters', value);
            } else {
                nextSearchParams.delete('filters');
            }

            return nextSearchParams;
        });

        lastPersistedValueRef.current = value || null;
        actualSection.current = pathname;
    }, [searchParams, pathname, setSearchParams]);

    const clearFilters = useCallback(() => {
        const currentValue = searchParams.get('filters');

        if (!currentValue) {
            return;
        }

        lastPersistedValueRef.current = null;
        setSearchParams((currentSearchParams) => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);
            nextSearchParams.delete('filters');
            return nextSearchParams;
        });
    }, [searchParams, setSearchParams]);

    const getFilters = useCallback(<T,>(paths: string[]): T => {
        detailsPathsRef.current = paths;
        const filters = searchParams.get('filters');
        return JSON.parse(filters || '{}') as T;
    }, [searchParams]);

    useEffect(() => {
        const isChangingSection = actualSection.current !== pathname;

        if (!isChangingSection) {
            return;
        }
        const isDetailsPath = detailsPathsRef.current.some((path) =>
            Boolean(matchPath({ path, end: true }, pathname))
        );

        if (!isDetailsPath && isChangingSection) {
            clearFilters();
            actualSection.current = '';
        }
    }, [clearFilters, pathname]);

    const memoizedValue = useMemo(() => ({
        updateFilters,
        clearFilters,
        getFilters
    }), [updateFilters, clearFilters, getFilters]);

    return (
        <TableFiltersPersistContext.Provider value={memoizedValue}>
            {children}
        </TableFiltersPersistContext.Provider>
    );
}

export const useTableFiltersPersistContext = (): TableFiltersPersistContextType => {
    const context = useContext(TableFiltersPersistContext);
    if (!context) {
        throw new Error('useTableFiltersPersistContext deve ser usado dentro de um TableFiltersPersistProvider');
    }
    return context;
};
